import type { LineItemI } from "./Pricing";
import { Item } from "./Item";
import type { Proto } from "./Proto";
type ServiceStatus = Proto.ServiceStatus;

export interface EventsI {
    event: EventI[];
}

export interface EventI {
    event_id?: number;
    event_name?: string;
    alien_uid?: string;
    exp_head_count?: number;
    reg_head_count?: number;
    event_priority?: number;
    event_title?: string;
    favorite?: string;
    node_type?: string;
    node_type_name?: string;
    event_type_id?: number;
    event_type_name?: string;
    state?: number;
    state_name?: string;
    version_number?: number;
    event_locator?: string;
    parent_id?: number;
    parent_name?: string;
    cabinet_id?: number;
    cabinet_name?: string;
    registration_url?: string;
    last_mod_user?: string;
    start_date?: string;
    end_date?: string;
    last_mod_dt?: string;
    creation_dt?: string;
    event_text?: EventText[];
    organization?: Organization[];
    profile?: EventProfile[];
    category?: EventCategory[];
    custom_attribute?: CustomAttribute[];
    role?: EventRole[];
    bill_item?: LineItemI[];
    relatedEvents?: EventI[];
    status?: "est" | "mod" | "del" | "new";

    [key: string]: any; //allow any other properties
}

export interface EventText {
    /*
     *      1: description
     *      2: note
     *      3: confirmation
     *      9: Location/Resource Request Info (rsrv prefs)
     */
    text_type_id?: number;
    text_type_name?: string;
    text?: string;
}

export interface Organization {
    organization_id?: number;
    organization_name?: string;
    organization_title?: string;
    primary?: string;
    organization_inherited?: number;
    organization_details?: OrganizationDetails;
}

export interface OrganizationDetails {
    account_number?: string;
    abbreviation?: string;
    organization_rating_id?: number;
    organization_rating?: string;
    organization_type?: OrganizationType;
}

export interface OrganizationType {
    organization_type_id?: number;
    organization_type_name?: string;
}

export interface EventProfile {
    profile_id?: number;
    ext_uid?: string;
    profile_name?: string;
    profile_code?: string;
    profile_description?: string;
    prof_use?: number;
    prof_use_name?: string;
    rec_type_id?: EventProfile.RecType;
    rec_type_name?: EventProfile.RecTypeName;
    pre_event?: string;
    post_event?: string;
    profile_comments?: string;
    occurrence_count?: number;
    init_start_dt?: string;
    init_end_dt?: string;
    expected_count?: number;
    registered_count?: number;
    setup_profile?: SetupProfile;
    ad_hoc_datelist?: AdHocDate[];
    binding_reservation?: BindingReservation[];
    reservation?: EventProfileReservation[];

    event?: EventI; // back reference
    crc?: string;
}

export namespace EventProfile {
    export const RecType = {
        SingleDateOrTime: 0, // AKA DNR (Do Not Repeat)
        RecurrenceGrammar: 1, // aka Pattern
        DateOrTimeList: 2, // aka Ad-Hoc
        FreeFormReservationSet: 3,
    } as const;
    export type RecType = (typeof RecType)[keyof typeof RecType];

    export const RecTypeName = {
        [RecType.SingleDateOrTime]: "Single Date/Time",
        SingleDateOrTime: "Single Date/Time",
        [RecType.RecurrenceGrammar]: "Recurrence Grammar",
        RecurrenceGrammar: "Recurrence Grammar",
        [RecType.DateOrTimeList]: "Date/Time List",
        DateOrTimeList: "Date/Time List",
        [RecType.FreeFormReservationSet]: "Free Form Reservation Set",
        FreeFormReservationSet: "Free Form Reservation Set",
    } as const satisfies { [key in keyof typeof RecType | RecType]: string };
    export type RecTypeName = (typeof RecTypeName)[keyof typeof RecTypeName];
}

export interface SetupProfile {
    setup_profile_id?: number;
    setup_tm?: string;
}

export interface AdHocDate {
    ad_hoc_start_dt?: string;
}

export interface BindingReservation {
    primary_reservation?: number;
    bound_name?: string;
    bound_event_id?: number;
    bound_reservation_id?: number;
    bound_reservation_ext_uid?: string;
}

export interface EventProfileReservation {
    reservation_id?: number;
    reservation_state?: number;
    rsrv_comments?: string;
    reservation_start_dt?: string;
    reservation_end_dt?: string;
    event_start_dt?: string;
    event_end_dt?: string;
    pre_event_dt?: string;
    post_event_dt?: string;
    space_reservation?: SpaceReservation[];
    resource_reservation?: ResourceReservation[];

    event?: EventI; // back reference
    profile?: EventProfile; // back reference
}

export interface SpaceReservation {
    space_id?: number;
    layout_id?: number;
    layout_name?: string;
    share?: string;
    attendance?: number;
    space_instructions?: string;
    rating?: number;
    default_layout_capacity?: number;
    selected_layout_capacity?: number;
    space?: Space;

    event?: EventI; // back reference
    profile?: EventProfile; // back reference
    reservation?: EventProfileReservation; // back reference
}

export interface Space {
    space_name?: string;
    formal_name?: string;
    max_capacity?: number;
    partition_name?: string;
    building_name?: string;
}

export interface ResourceReservation {
    resource_id?: number;
    resource_instructions?: string;
    quantity?: number;
    resource?: Resource;

    event?: EventI; // back reference
    profile?: EventProfile; // back reference
    reservation?: EventProfileReservation; // back reference
}

export interface Resource {
    resource_name?: string;
}

export interface EventCategory {
    category_id?: number;
    category_name?: string;
    category_defn_state?: number;
    category_sort_order?: number;
}

export interface CustomAttribute {
    attribute_id?: number;
    attribute_name?: string;
    attribute_type?: string;
    attribute_type_name?: string;
    attribute_value?: string;
    attribute_defn_state?: number;
    attribute_sort_order?: number;
    multi_val?: number;
    attribute_contact?: AttributeContact;
    attribute_resource?: AttributeResource;
    attribute_space?: AttributeSpace;
}

export interface AttributeContact {
    attribute_contact_id?: number;
    attribute_contact_name?: string;
}

export interface AttributeResource {
    attribute_resource_id?: number;
    attribute_resource_name?: string;
}

export interface AttributeSpace {
    attribute_space_id?: number;
    attribute_space_name?: string;
}
export interface EventRole {
    role_id?: number;
    role_name?: string;
    role_sort_order?: number;
    contact?: EventContact;
}

export interface EventContact {
    contact_id?: number;
    contact_name?: string;
    contact_first_name?: string;
    contact_middle_name?: string;
    contact_last_name?: string;
    formatted_address?: string;
    phone?: string;
    fax?: string;
    email?: string;
    status?: ServiceStatus;
}

export interface EventOwner {
    object_id: number;
    object_type: Item.Ids;
    owner_cont_id: number;
    owner_name: string;
    owner_username: string;
    status: ServiceStatus;
}
